import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, formatEUR } from "@/lib/api";
import { MobileShell, CustomerBottomNav } from "@/components/Shell";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, ArrowRight, MessageCircle, Check, Sparkles, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const QUICK_AMOUNTS = [2, 5, 10];

export default function StaffProfile() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const locationId = params.get("location");
  const { user } = useAuth();
  const [staff, setStaff] = useState(null);
  const [amount, setAmount] = useState(5);
  const [customAmount, setCustomAmount] = useState("");
  const [message, setMessage] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [step, setStep] = useState("amount"); // amount | tap | success
  const [tipResult, setTipResult] = useState(null);
  const [favs, setFavs] = useState([]);

  useEffect(() => {
    api.get(`/staff/${id}`).then((r) => setStaff(r.data));
    if (user?.role === "customer") {
      api.get("/favorites").then((r) => setFavs(r.data.map((f) => f.staff_id))).catch(() => {});
    }
  }, [id, user]);

  const isFav = favs.includes(id);

  async function toggleFavorite() {
    if (!user) return;
    if (isFav) {
      await api.delete(`/favorites/${id}`);
      setFavs((f) => f.filter((x) => x !== id));
    } else {
      await api.post(`/favorites/${id}`);
      setFavs((f) => [...f, id]);
    }
  }

  async function submitTip() {
    const finalAmount = parseFloat(customAmount || amount);
    if (!finalAmount || finalAmount <= 0) return;
    setStep("tap");
    // Simulate tap-to-pay delay
    await new Promise((r) => setTimeout(r, 1800));
    try {
      const { data } = await api.post("/tips", {
        staff_id: id,
        location_id: locationId || null,
        amount: finalAmount,
        message,
        customer_name: customerName,
      });
      setTipResult(data);
      // Refresh loyalty count for animation
      let loyalty = null;
      try { const r = await api.get("/loyalty/me"); loyalty = r.data; } catch {}
      setTipResult({ ...data, loyalty });
      setStep("success");
    } catch (e) {
      setStep("amount");
    }
  }

  if (!staff) return <MobileShell><div className="p-10 text-center text-[#A1A1AA]">Lade…</div></MobileShell>;

  return (
    <MobileShell>
      {/* Hero */}
      <div className="relative">
        <div className="h-44 bg-gradient-to-br from-[#1A1A20] to-[#0d0d11]"></div>
        <div className="absolute top-0 left-0 right-0 p-5 flex justify-between items-start">
          <Link to={locationId ? `/l/${locationId}` : "/scan"} className="text-xs text-white/80 tracking-[0.2em] uppercase bg-black/40 backdrop-blur px-3 py-1.5 rounded-full">← Zurück</Link>
          {user?.role === "customer" && (
            <button onClick={toggleFavorite} data-testid="staff-favorite-button"
              className={`p-2 rounded-full backdrop-blur transition-colors ${isFav ? "bg-[#E05A33]/20 text-[#E05A33]" : "bg-black/40 text-white"}`}>
              <Heart className={`w-5 h-5 ${isFav ? "fill-current" : ""}`} />
            </button>
          )}
        </div>
        <img src={staff.photo_url || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=300"}
          alt={staff.name}
          className="absolute -bottom-12 left-1/2 -translate-x-1/2 w-32 h-32 rounded-full object-cover border-4 border-[#0A0A0C] gold-glow" />
      </div>

      <div className="px-5 pt-16 pb-8 text-center">
        <h1 className="text-3xl font-black tracking-tight" data-testid="staff-name">{staff.name}</h1>
        <p className="text-[#A1A1AA] mt-2 max-w-xs mx-auto" data-testid="staff-bio">{staff.bio}</p>
      </div>

      {/* Tip flow */}
      <AnimatePresence mode="wait">
        {step === "amount" && (
          <motion.div key="amount" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="px-5 space-y-6">
            <div className="bg-[#121216] border border-white/5 rounded-2xl p-6">
              <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] text-center mb-4">Schnellbetrag</div>
              <div className="grid grid-cols-3 gap-3">
                {QUICK_AMOUNTS.map((a) => (
                  <button key={a} onClick={() => { setAmount(a); setCustomAmount(""); }} data-testid={`tip-quick-${a}`}
                    className={`py-5 rounded-xl border-2 text-2xl font-black transition-all ${
                      amount === a && !customAmount
                        ? "border-[#EBB300] bg-[#EBB300]/10 text-[#EBB300] scale-105 gold-glow"
                        : "border-white/10 bg-[#0A0A0C] text-white hover:border-[#EBB300]/40"
                    }`}>
                    {a}€
                  </button>
                ))}
              </div>
              <div className="ticket-divider my-5"></div>
              <label className="block">
                <span className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-2 block">Eigener Betrag</span>
                <div className="relative">
                  <input type="number" min="0.5" step="0.5" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} placeholder="0,00"
                    data-testid="tip-custom-amount-input"
                    className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-4 py-3 pr-10 text-white text-xl focus:border-[#EBB300]" />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A1A1AA]">€</span>
                </div>
              </label>
            </div>

            {!user && (
              <div className="bg-[#121216] border border-white/5 rounded-2xl p-5">
                <span className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-2 block">Dein Name (optional)</span>
                <input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Gast"
                  data-testid="tip-customer-name-input"
                  className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#EBB300]" />
              </div>
            )}

            <div className="bg-[#121216] border border-white/5 rounded-2xl p-5">
              <label className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-2">
                <MessageCircle className="w-3.5 h-3.5" /> Nachricht (optional)
              </label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder="Danke für den tollen Service!" rows={3}
                data-testid="tip-message-input"
                className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#EBB300] resize-none" />
            </div>

            <button onClick={submitTip} data-testid="tip-submit-button"
              className="w-full inline-flex justify-center items-center gap-2 px-6 py-4 rounded-full bg-[#EBB300] text-[#0A0A0C] font-bold text-base hover:bg-[#FAD02C] transition-all gold-glow">
              {formatEUR(parseFloat(customAmount || amount) || 0)} senden
              <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-center text-[10px] tracking-[0.2em] uppercase text-[#52525B]">Sicher per Tap-to-Pay</p>
          </motion.div>
        )}

        {step === "tap" && (
          <motion.div key="tap" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="px-5 py-10 flex flex-col items-center justify-center">
            <div className="relative w-40 h-40 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border-2 border-[#EBB300] pulse-ring"></div>
              <div className="absolute inset-0 rounded-full border-2 border-[#EBB300] pulse-ring" style={{ animationDelay: "0.5s" }}></div>
              <motion.div animate={{ scale: [1, 0.95, 1] }} transition={{ duration: 1.2, repeat: Infinity }}
                className="w-28 h-28 rounded-full bg-[#EBB300] flex items-center justify-center text-[#0A0A0C] gold-glow">
                <Loader2 className="w-10 h-10 animate-spin" strokeWidth={2.5} />
              </motion.div>
            </div>
            <div className="text-center mt-8">
              <div className="text-xs tracking-[0.3em] uppercase text-[#EBB300]">Halte dein Gerät bereit</div>
              <div className="text-2xl font-black mt-3 tracking-tight">Tap-to-Pay läuft …</div>
              <div className="text-[#A1A1AA] mt-2 text-sm">Halte dein Gerät an den Sensor</div>
            </div>
          </motion.div>
        )}

        {step === "success" && tipResult && (
          <motion.div key="success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="px-5 pb-10 space-y-6">
            <div className="relative bg-[#121216] border border-[#22C55E]/30 rounded-2xl p-8 text-center overflow-hidden">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 12 }}
                className="w-20 h-20 rounded-full bg-[#22C55E]/20 mx-auto flex items-center justify-center mb-4">
                <Check className="w-10 h-10 text-[#22C55E]" strokeWidth={2.5} />
              </motion.div>
              <div className="text-xs tracking-[0.25em] uppercase text-[#22C55E] mb-2">Trinkgeld gesendet</div>
              <div className="text-5xl font-black tracking-tight text-white" data-testid="tip-success-amount">{formatEUR(tipResult.amount)}</div>
              <div className="text-[#A1A1AA] mt-3">an {tipResult.staff_name}</div>

              {tipResult.loyalty && tipResult.loyalty.tip_count > 0 && (
                <div className="mt-6 pt-6 border-t border-white/5">
                  <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Deine Stempelkarte</div>
                  <div className="flex justify-center gap-2">
                    {Array.from({ length: tipResult.loyalty.stamps_per_card }).map((_, i) => {
                      const filled = i < tipResult.loyalty.stamps || (tipResult.loyalty.stamps === 0 && tipResult.loyalty.freeshots > 0);
                      return (
                        <motion.div key={i}
                          initial={{ scale: 0.8 }} animate={{ scale: 1 }} transition={{ delay: i * 0.1 }}
                          className={`w-9 h-9 rounded-full border-2 ${filled ? "stamp-punched border-[#EBB300] flex items-center justify-center" : "border-white/10 bg-[#0A0A0C]"}`}>
                          {filled && <Sparkles className="w-3.5 h-3.5 text-[#0A0A0C]" />}
                        </motion.div>
                      );
                    })}
                  </div>
                  {tipResult.loyalty.stamps === 0 && tipResult.loyalty.freeshots > 0 && (
                    <motion.div initial={{ scale: 0, rotate: -10 }} animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", delay: 0.5 }}
                      className="mt-5 inline-block px-5 py-3 rounded-full bg-[#E05A33] text-white font-black tracking-tight text-lg gold-glow">
                      🥃 Freeshot gewonnen!
                    </motion.div>
                  )}
                </div>
              )}
            </div>

            <Link to="/app" data-testid="tip-success-continue"
              className="block text-center w-full px-6 py-3.5 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-colors">
              Zurück zur Startseite
            </Link>
            <button onClick={() => { setStep("amount"); setMessage(""); setTipResult(null); }} data-testid="tip-success-again"
              className="block text-center w-full px-6 py-3.5 rounded-full bg-[#EBB300] text-[#0A0A0C] font-bold hover:bg-[#FAD02C] transition-colors">
              Noch ein Trinkgeld
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <CustomerBottomNav />
    </MobileShell>
  );
}
